import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Package, ShoppingBag, Star, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (p) => `R${Number(p).toLocaleString('en-ZA', { minimumFractionDigits: 0 })}`;

const CATEGORIES = [
  { name: "Bags", image: "https://images.unsplash.com/photo-1548036328-c9fa89d128fa?w=800&q=80", desc: "Iconic silhouettes" },
  { name: "Shoes", image: "https://customer-assets.emergentagent.com/job_heir-luxx-preview/artifacts/lvww560k_Gucci%20Sneaker.jpg", desc: "From pumps to sneakers" },
  { name: "Clothing", image: "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=800&q=80", desc: "Editorial pieces" },
  { name: "Accessories", image: "https://images.unsplash.com/photo-1526045612212-70caf35c14df?w=800&q=80", desc: "The finishing touch" },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [favIds, setFavIds] = useState([]);
  const { user } = useAuth();
  const carouselRef = useRef(null);

  const scrollCarousel = (dir) => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: dir * 284, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    // API and axios are module-level constants — stable across renders, intentionally omitted from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    axios.get(`${API}/products/featured`).then(r => setFeatured(r.data)).catch(() => {});
    axios.get(`${API}/products?sort_by=newest&limit=8`).then(r => setNewArrivals(r.data?.products || [])).catch(() => {});
    axios.get(`${API}/products/designers`).then(r => setDesigners(r.data)).catch(() => {});
    if (user) {
      axios.get(`${API}/favorites/ids`, { withCredentials: true }).then(r => setFavIds(r.data)).catch(() => {});
    }
  }, [user]);

  const toggleFav = async (productId) => {
    if (!user) return;
    if (favIds.includes(productId)) {
      await axios.delete(`${API}/favorites/${productId}`, { withCredentials: true });
      setFavIds(ids => ids.filter(id => id !== productId));
    } else {
      await axios.post(`${API}/favorites`, { product_id: productId }, { withCredentials: true });
      setFavIds(ids => [...ids, productId]);
    }
  };

  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative h-screen min-h-[600px] flex items-center overflow-hidden" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/90f98569-40ac-4edc-a183-4a0b130d6859/images/059abb168abf39e30e694e29fa0e11ae3a10a32ad71568308db06e0fdb8c0763.png)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full pt-20">
          <div className="max-w-xl">
            <p className="text-xs uppercase tracking-[0.4em] text-[#D4AF37] font-semibold mb-5 font-work animate-fade-in">
              The Luxury Personal Shopper
            </p>
            <img
              src="https://customer-assets.emergentagent.com/job_heir-luxx-preview/artifacts/c7aul43d_A098BA3C-35F8-4771-9E64-6F8CEEABD35B.png"
              alt="HEIR LUXX"
              className="mb-6 w-72 sm:w-80 lg:w-96"
            />
            <p className="text-white/75 text-base mb-10 max-w-sm font-work leading-relaxed">
              Curated luxury from the world's finest boutiques. Shop with confidence through your dedicated personal shopper.
            </p>
            <div className="flex gap-4">
              <Link to="/shop">
                <button className="bg-white text-[#0A0A0A] hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors duration-300 font-semibold px-8 py-4 text-xs uppercase tracking-widest font-work" data-testid="hero-cta">
                  Discover Collection
                </button>
              </Link>
              <Link to="/designers">
                <button className="border border-white/60 text-white hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-300 px-8 py-4 text-xs uppercase tracking-widest font-work">
                  Our Designers
                </button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
          <div className="w-px h-12 bg-white/40" />
          <p className="text-white/40 text-[10px] uppercase tracking-[0.3em] font-work">Scroll</p>
        </div>
      </section>

      {/* NEW ARRIVALS — Horizontal Carousel (shown FIRST after hero) */}
      {newArrivals.length > 0 && (
        <section className="py-24 bg-[#F5F5F5]" data-testid="new-arrivals-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">

            {/* Header row */}
            <div className="flex items-end justify-between mb-12">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles size={12} className="text-[#D4AF37]" />
                  <p className="text-xs uppercase tracking-[0.4em] text-[#D4AF37] font-semibold font-work">
                    Just Dropped
                  </p>
                </div>
                <h2 className="font-playfair text-4xl text-[#0A0A0A] tracking-tight">New Arrivals</h2>
                <p className="text-[#999999] text-sm font-work mt-2 tracking-wide">
                  {newArrivals.length} latest pieces added to the collection
                </p>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => scrollCarousel(-1)}
                  className="w-10 h-10 border border-[#E5E5E5] text-[#AAAAAA] hover:border-[#D4AF37] hover:text-[#D4AF37] flex items-center justify-center transition-all"
                  data-testid="arrivals-prev-btn"
                  aria-label="Previous"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  onClick={() => scrollCarousel(1)}
                  className="w-10 h-10 border border-[#E5E5E5] text-[#AAAAAA] hover:border-[#D4AF37] hover:text-[#D4AF37] flex items-center justify-center transition-all"
                  data-testid="arrivals-next-btn"
                  aria-label="Next"
                >
                  <ChevronRight size={18} />
                </button>
                <Link
                  to="/shop?sort_by=newest"
                  className="hidden sm:flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#AAAAAA] hover:text-[#D4AF37] transition-colors font-work ml-2"
                  data-testid="new-arrivals-view-all"
                >
                  View All <ArrowRight size={14} />
                </Link>
              </div>
            </div>

            {/* Scrollable strip */}
            <div
              ref={carouselRef}
              className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-4"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              data-testid="arrivals-carousel"
            >
              {newArrivals.map((p, i) => (
                <div
                  key={p.product_id}
                  className="flex-shrink-0 w-[250px] sm:w-[268px] snap-start"
                  style={{ animation: `arrivalFadeIn 0.5s ease ${i * 0.06}s both` }}
                  data-testid={`arrival-card-${p.product_id}`}
                >
                  {/* Gold accent line above each card */}
                  <div className="h-0.5 bg-gradient-to-r from-[#D4AF37]/60 to-transparent mb-2" />
                  <ProductCard
                    product={p}
                    isFav={favIds.includes(p.product_id)}
                    onToggleFav={() => toggleFav(p.product_id)}
                  />
                </div>
              ))}

              {/* "See more" end tile */}
              <div className="flex-shrink-0 w-[200px] snap-start flex items-center justify-center">
                <Link
                  to="/shop?sort_by=newest"
                  className="flex flex-col items-center gap-4 text-center group"
                  data-testid="arrivals-see-more"
                >
                  <div className="w-16 h-16 border border-[#E5E5E5] group-hover:border-[#D4AF37] flex items-center justify-center transition-all">
                    <ArrowRight size={20} className="text-[#AAAAAA] group-hover:text-[#D4AF37] transition-colors" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-[#AAAAAA] group-hover:text-[#D4AF37] transition-colors font-work">View All</p>
                    <p className="text-[10px] text-[#CCCCCC] font-work mt-1">New Arrivals</p>
                  </div>
                </Link>
              </div>
            </div>

            {/* Mobile CTA */}
            <div className="mt-10 text-center sm:hidden">
              <Link to="/shop?sort_by=newest">
                <button className="border border-[#CCCCCC] text-[#555555] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors px-8 py-3 text-xs uppercase tracking-widest font-work">
                  View All New Arrivals
                </button>
              </Link>
            </div>

          </div>
        </section>
      )}

      {/* FEATURED PRODUCTS */}
      <section className="py-24 px-6 lg:px-12 max-w-7xl mx-auto" data-testid="featured-section">
        <div className="flex items-end justify-between mb-14">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-3 font-work">Curated Selection</p>
            <h2 className="font-playfair text-4xl text-[#0A0A0A] tracking-tight">Featured Pieces</h2>
          </div>
          <Link to="/shop" className="hidden sm:flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#0A0A0A] hover:text-[#D4AF37] transition-colors font-work">
            View All <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 gap-y-12">
          {featured.map(p => (
            <ProductCard
              key={p.product_id}
              product={p}
              isFav={favIds.includes(p.product_id)}
              onToggleFav={() => toggleFav(p.product_id)}
            />
          ))}
        </div>
      </section>

      {/* DESIGNERS */}
      {designers.length > 0 && (
        <section className="py-16 border-y border-[#E5E5E5] overflow-hidden bg-[#F9F9F9]">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 mb-8">
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold text-center font-work">Our Designers</p>
          </div>
          <div className="flex gap-12 animate-marquee whitespace-nowrap" style={{ animation: 'marquee 30s linear infinite' }}>
            {[...designers, ...designers].map((d, i) => (
              <Link key={`${d}-${i}`} to={`/shop?designer=${encodeURIComponent(d)}`} className="font-playfair text-2xl text-[#0A0A0A]/30 hover:text-[#D4AF37] transition-colors flex-shrink-0">
                {d}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* CATEGORIES */}
      <section className="py-24 px-6 lg:px-12 max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-3 font-work">Shop By Category</p>
          <h2 className="font-playfair text-4xl text-[#0A0A0A] tracking-tight">Explore The Edit</h2>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {CATEGORIES.map(cat => (
            <Link key={cat.name} to={`/shop?category=${cat.name}`} className="group relative aspect-[3/4] overflow-hidden bg-[#F9F9F9]" data-testid={`category-${cat.name.toLowerCase()}`}>
              <img src={cat.image} alt={cat.name} className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6">
                <p className="font-playfair text-xl text-white font-semibold">{cat.name}</p>
                <p className="text-white/70 text-xs font-work mt-1">{cat.desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 bg-[#F9F9F9] border-y border-[#E5E5E5]" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-3 font-work">The Process</p>
            <h2 className="font-playfair text-4xl text-[#0A0A0A] tracking-tight">How It Works</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              { icon: <Star size={28} className="text-[#D4AF37]" />, step: "01", title: "Discover & Save", desc: "Browse our curated catalog of luxury items. Save favorites and add to your personal bag." },
              { icon: <ShoppingBag size={28} className="text-[#D4AF37]" />, step: "02", title: "Request Purchase", desc: "Submit your bag. Your personal shopper verifies availability and sources your items." },
              { icon: <Package size={28} className="text-[#D4AF37]" />, step: "03", title: "Pay & Receive", desc: "Receive your secure payment link. Items are quality-checked, packed, and shipped to you." },
            ].map(item => (
              <div key={item.step} className="flex flex-col gap-5">
                <div className="flex items-center gap-4">
                  {item.icon}
                  <span className="font-playfair text-5xl text-[#0A0A0A]/10 font-bold">{item.step}</span>
                </div>
                <h3 className="font-playfair text-xl text-[#0A0A0A]">{item.title}</h3>
                <p className="text-[#888888] text-sm leading-relaxed font-work">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EDITORIAL BANNER */}
      <section className="relative h-[500px] overflow-hidden">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/90f98569-40ac-4edc-a183-4a0b130d6859/images/75cac658cad1e8de44eabf90c0bdde301a76d72b37e08540f11caaeb7ef333d0.png"
          alt="Luxury"
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
          <div className="text-center">
            <p className="text-xs uppercase tracking-[0.4em] text-[#D4AF37] font-semibold mb-4 font-work">Exclusive Access</p>
            <h2 className="font-playfair text-4xl sm:text-5xl text-white mb-8">The World's Finest. Delivered.</h2>
            <Link to="/shop">
              <button className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-white transition-colors px-10 py-4 text-xs uppercase tracking-widest font-semibold font-work">
                Shop The Edit
              </button>
            </Link>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes arrivalFadeIn {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
